import {
  DESTINATION_LIMIT_MESSAGE as CONTRACT_LIMIT_MESSAGE,
  SHIPPING_ADDRESS_MAX_LENGTHS,
  type ShippingFeeSettings,
} from '@repo/api-contracts';
import { estimateShipping } from './estimate';
import {
  checkQuoteRequestRules,
  parseQuoteQuantity,
  type QuoteRequestRuleContext,
  type QuoteRequestRuleErrors,
} from './quote-request-rules';
import type { SizeQuantityRow } from './size-allocation';

/**
 * 견적 요청 배송지 카드 규칙(SUPER-86 spec §5-1·§6-1) — 회원(`features/quote-request`)과 비회원(`features/guest-quote-request`)
 * 요청이 같은 칸 이름·문구·경로 해석을 쓰도록 한곳에 둔다(같은 계층 feature 끼리는 import 할 수 없다).
 */

/** 배송지 카드의 칸 — 사이즈 상품의 sizes는 회원·비회원 공용이다. */
export const DESTINATION_FIELD_KEYS = [
  'recipientName',
  'recipientPhone',
  'postalCode',
  'address1',
  'address2',
  'quantity',
  'sizes',
] as const;

export type DestinationFieldKey = (typeof DESTINATION_FIELD_KEYS)[number];

export type DestinationFieldErrors = Partial<Record<DestinationFieldKey, string>>;

/** 배송지 목록 자체의 오류(개수·합계 등 카드 하나로 보낼 수 없는 오류). */
export const DESTINATION_LIST_MESSAGE = '배송지를 확인해 주세요.';
/**
 * 서버가 배송지 사이즈(`destinations.{i}.sizes`)를 거절할 때의 안내 — 폼을 연 뒤 상품 사이즈 옵션이 바뀐 경우다(이 폼의 로컬 검사를
 * 통과했으므로). 사이즈 줄 편집기가 숨은 폼(배송지 1곳·사이즈 없이 열린 폼)에서도 보이도록 배송지 묶음 오류로도 쓴다.
 */
export const SIZE_OPTIONS_CHANGED_MESSAGE =
  '상품의 사이즈 구성이 바뀌었습니다. 페이지를 새로고침한 뒤 사이즈별 수량을 다시 입력해 주세요.';
/** 10곳 초과 안내 — spec 문구 그대로(마침표 없음), 계약 스키마의 max 문구와 같다. */
export const DESTINATION_LIMIT_MESSAGE: string = CONTRACT_LIMIT_MESSAGE;
export const POSTAL_CODE_FORMAT_MESSAGE = '우편번호는 5자리 숫자로 입력해 주세요.';
/** 임시 견적 — 우편번호를 아직 고르지 않은 카드는 기본 배송비로 센다. */
export const PENDING_ADDRESS_HINT = '주소를 고르면 지역 추가 요금이 반영됩니다';
/** 비회원 배송지 묶음 머리 안내(spec D8). */
export const GUEST_DESTINATION_HINT = '받는 분·연락처·상세주소는 주문할 때 입력합니다';

const INVALID_MESSAGES: Readonly<Record<DestinationFieldKey, string>> = {
  recipientName: '수령인 이름을 입력해 주세요.',
  recipientPhone: '수령인 연락처를 입력해 주세요.',
  postalCode: '우편번호를 입력해 주세요.',
  address1: '주소를 입력해 주세요.',
  address2: '상세 주소를 입력해 주세요.',
  quantity: '수량은 1 이상의 정수로 입력해 주세요.',
  sizes: '사이즈를 한 개 이상 넣어 주세요.',
};

const TOO_LONG_MESSAGES: Readonly<Partial<Record<DestinationFieldKey, string>>> = {
  recipientName: `수령인 이름은 ${SHIPPING_ADDRESS_MAX_LENGTHS.recipientName}자 이하로 입력해 주세요.`,
  recipientPhone: `수령인 연락처는 ${SHIPPING_ADDRESS_MAX_LENGTHS.recipientPhone}자 이하로 입력해 주세요.`,
  address1: `주소는 ${SHIPPING_ADDRESS_MAX_LENGTHS.address1}자 이하로 입력해 주세요.`,
  address2: `상세 주소는 ${SHIPPING_ADDRESS_MAX_LENGTHS.address2}자 이하로 입력해 주세요.`,
};

const isDestinationFieldKey = (key: unknown): key is DestinationFieldKey =>
  typeof key === 'string' && (DESTINATION_FIELD_KEYS as readonly string[]).includes(key);

const INDEX_PATTERN = /^\d+$/;

const indexOf = (raw: PropertyKey | undefined): number | null => {
  if (typeof raw === 'number') return Number.isInteger(raw) && raw >= 0 ? raw : null;
  return typeof raw === 'string' && INDEX_PATTERN.test(raw) ? Number(raw) : null;
};

type SizeIssueLeaf = 'list' | 'quantity' | 'sizeOptionId';

/**
 * zod issue path(`['destinations', 2, 'postalCode']`) 또는 서버 점 경로(`'destinations.2.postalCode'`, 교차 조정 R3) → 카드 위치·칸.
 * 목록 자체(`destinations` 한 마디)·다른 칸·모르는 칸은 null 이다.
 */
export const destinationIssueTarget = (
  path: readonly PropertyKey[] | string,
): {
  readonly index: number;
  readonly key: DestinationFieldKey;
  readonly leaf?: SizeIssueLeaf;
} | null => {
  const parts: readonly PropertyKey[] = typeof path === 'string' ? path.split('.') : path;
  const [head, rawIndex, key] = parts;
  if (head !== 'destinations' || !isDestinationFieldKey(key)) return null;
  const index = indexOf(rawIndex);
  if (index === null) return null;
  if (key === 'sizes') {
    if (parts.length === 3) return { index, key, leaf: 'list' };
    const rowIndex = indexOf(parts[3]);
    const leaf = parts[4];
    return parts.length === 5 &&
      rowIndex !== null &&
      (leaf === 'quantity' || leaf === 'sizeOptionId')
      ? { index, key, leaf }
      : null;
  }
  return parts.length === 3 ? { index, key } : null;
};

/**
 * 카드 칸 문구 — 계약 스키마에는 한글 문구가 없어 여기서 붙인다. 빈 우편번호도 형식 검사(5자리 숫자)에 걸리므로 값이 있을 때만
 * 형식 안내를 하고, 너무 길면 글자 수 문구, 그 밖에는 입력 안내다.
 */
export const destinationFieldMessage = (
  key: DestinationFieldKey,
  issueCode: string,
  rawValue: string,
  extra?: { readonly leaf?: SizeIssueLeaf; readonly issueMessage?: string },
): string => {
  // sizes는 배열이므로 rawValue 대신 경로의 leaf와 계약 문구로 안내한다.
  if (key === 'sizes') {
    if (extra?.leaf === 'quantity') return '사이즈 수량은 1 이상의 정수로 입력해 주세요.';
    if (extra?.leaf === 'sizeOptionId') return '사이즈를 골라 주세요.';
    return issueCode === 'too_small'
      ? INVALID_MESSAGES.sizes
      : (extra?.issueMessage ?? INVALID_MESSAGES.sizes);
  }
  if (key === 'postalCode' && rawValue.trim() !== '') return POSTAL_CODE_FORMAT_MESSAGE;
  return issueCode === 'too_big'
    ? (TOO_LONG_MESSAGES[key] ?? INVALID_MESSAGES[key])
    : INVALID_MESSAGES[key];
};

/** 카드 수량 문자열 합계(= 견적 수량, spec D5) — 카드가 없거나 하나라도 1 이상 정수가 아니면 null. */
export const sumDestinationQuantities = (raws: readonly string[]): number | null => {
  if (raws.length === 0) return null;
  let total = 0;
  for (const raw of raws) {
    const quantity = parseQuoteQuantity(raw);
    if (!Number.isInteger(quantity) || quantity < 1) return null;
    total += quantity;
  }
  return total;
};

/** 카드 key 와 칸 원문 — 회원·비회원 폼의 `DestinationDraft` 와 같은 모양(칸 일부만 있어도 된다). */
interface DestinationCardInput {
  readonly key: string;
  readonly value: Readonly<Partial<Record<Exclude<DestinationFieldKey, 'sizes'>, string>>> & {
    readonly sizes?: readonly SizeQuantityRow[];
  };
}

export interface DestinationErrorEntry {
  readonly cardKey: string;
  readonly field: DestinationFieldKey;
  readonly message: string;
}

/**
 * 오류 경로(zod issue path 또는 서버 점 키) → 그 카드 key·칸·우리 문구. 오류는 index 가 아니라 **카드 key** 로 붙인다 — 제출 때의
 * 카드 순서로 key 를 찾으므로, 오류 뒤 다른 카드를 지워도 오류가 옮겨 붙지 않는다. 카드 칸이 아니거나 그 위치에 카드가 없으면 null.
 */
export const destinationErrorEntry = (
  path: readonly PropertyKey[] | string,
  issueCode: string,
  cards: readonly DestinationCardInput[],
  issueMessage?: string,
): DestinationErrorEntry | null => {
  const target = destinationIssueTarget(path);
  const card = target === null ? undefined : cards[target.index];
  if (target === null || card === undefined) return null;
  return {
    cardKey: card.key,
    field: target.key,
    message: destinationFieldMessage(
      target.key,
      issueCode,
      target.key === 'sizes' ? '' : (card.value[target.key] ?? ''),
      { leaf: target.leaf, issueMessage },
    ),
  };
};

/** 제출 검증 중에 채우는 카드 key → 칸 오류. */
export type MutableDestinationErrors = Record<string, DestinationFieldErrors>;

/** 카드 오류를 더한다 — 같은 칸에 먼저 온 오류를 둔다. */
export const addDestinationError = (
  errors: MutableDestinationErrors,
  cardKey: string,
  field: DestinationFieldKey,
  message: string,
): void => {
  const card = errors[cardKey] ?? {};
  if (card[field] === undefined) errors[cardKey] = { ...card, [field]: message };
};

/** 배송지 목록 자체의 스키마 오류 문구 — 10곳 초과(`too_big`)는 한도 문구, 그 밖(빈 목록·합계)은 일반 문구. */
export const destinationListMessage = (issueCode: string): string =>
  issueCode === 'too_big' ? DESTINATION_LIMIT_MESSAGE : DESTINATION_LIST_MESSAGE;

interface DestinationRuleCard {
  readonly value: { readonly quantity: string; readonly postalCode: string };
}

/**
 * 스키마 밖 규칙을 **배송지 수량 합계**에 건다(spec D5) — 최소 주문 수량·견적 금액 상한(상품 금액 + 임시 견적과 같은 배송비, 서버 초안
 * 규칙과 같다)·희망 납기일. 카드 수량이 하나라도 정수가 아니면 합계 규칙은 건너뛴다(그 카드의 스키마 오류가 먼저 보인다).
 */
export const checkDestinationTotalRules = (
  destinations: readonly DestinationRuleCard[],
  desiredDate: string,
  settings: ShippingFeeSettings,
  context: QuoteRequestRuleContext,
): QuoteRequestRuleErrors => {
  const total = sumDestinationQuantities(destinations.map((item) => item.value.quantity));
  const shipping = estimateShipping(
    destinations.map((item) => item.value.postalCode),
    settings,
  );
  return checkQuoteRequestRules(
    {
      quantity: total === null ? '' : String(total),
      desiredDate,
      shippingAmount: shipping.shippingAmount,
    },
    context,
  );
};
